import React from "react";
import FiltersMenu, {
  DropdownOption,
  SelectedItems,
} from "../components/FiltersMenu";
import { useTranslation } from "react-i18next";
import { productSearchStore as store } from "../ProductSearchStore";
import { observer } from "mobx-react-lite";

const ProductFilters = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<DropdownOption[]>([]);

  const findNodeById = (
    nodes: DropdownOption[],
    id?: string
  ): DropdownOption | null => {
    for (const node of nodes) {
      if (node.id === id) return node;

      if (node.children?.length) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const getAllDescendants = (node: DropdownOption): DropdownOption[] => {
    let result: DropdownOption[] = [];

    for (const child of node.children ?? []) {
      result.push(child);
      result = result.concat(getAllDescendants(child));
    }

    return result;
  };

  function findNodeBySearchTerm(
    nodes: DropdownOption[],
    searchTerm: string,
    parent: DropdownOption | undefined = undefined,
    result: DropdownOption[] = []
  ) {
    const lowerSearch = searchTerm.toLowerCase();

    nodes.forEach((node) => {
      if (node.name.toLowerCase().includes(lowerSearch) && !node.isCategory) {
        result.push({
          ...node,
          parent: parent,
        });
      }

      if (node.children?.length) {
        findNodeBySearchTerm(node.children, searchTerm, node, result);
      }
    });

    return result;
  }

  const getFilterOptions = async (id?: string, searchTerm?: string) => {
    const rootNodes = filters;

    if (searchTerm) {
      const filteredItems = findNodeBySearchTerm(rootNodes, searchTerm);
      return {
        count: filteredItems?.length ?? 0,
        items: filteredItems ?? [],
      };
    }

    const selectedNode = findNodeById(rootNodes, id);

    if (!selectedNode) {
      return { count: 0, items: [] };
    }

    return {
      count: selectedNode.children?.length ?? 0,
      items: selectedNode.children ?? [],
    };
  };

  const handleSelectFilter = (items: SelectedItems[]) => {
    store.setSelectedFilters(items);
    store.searchProducts();
  };

  async function init() {
    await store.fetchFilterCategories();

    const filterCategories = Object.entries(store.filtersOptions).map(
      ([key, value]) => {
        const nameMapping: { [key: string]: string } = {
          DIN_categories_level_1: "DIN 276 Level 1",
          DIN_categories_level_2: "DIN 276 Level 2",
          DIN_categories_level_3: "DIN 276 Level 3",
          materials: "Material",
          conformities: `EPD ${t("mapping.conformity")}`,
          certifications: t("mapping.certification"),
          reference_units: t("mapping.referenceUnit"),
          publication_status: t("productSearch.publicationStatus"),
          other_properties: t("mapping.otherProperties"),
        };

        return {
          id: key,
          name: nameMapping[key],
          children: value,
          isCategory: true,
          hidden: key === "publication_status" && !store.isManufacturerLicence,
        };
      }
    );

    setFilters(filterCategories);
  }

  React.useEffect(() => {
    init();
  }, []);

  return (
    <div id="filter-wrapper" className="relative">
      <FiltersMenu
        wrapperID="filter-wrapper"
        options={filters.filter((rec) => !rec.hidden)}
        fetchChildren={getFilterOptions}
        pageSize={25}
        onSelectItem={handleSelectFilter}
        checkedItems={store.selectedFilters}
        noResultText={t("productEditor.noFilterOptionText")}
      />
    </div>
  );
};

export default observer(ProductFilters);
